import React, { useCallback, useMemo, useState } from "react";
import {
	CCAvatar,
	CCBadge,
	CCBadgeTone,
	CCButton,
	CCField,
	CCMenu,
	CCModal,
	CCSegmented,
	CCSegmentedOption,
	useToast,
} from "@pymblecitizencoders/cc-ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
/* The REGULAR set, which is where an outline circle lives — the solid one is
   filled, and a filled dot reads as a decision rather than the absence of one. */
import { faCircle } from "@fortawesome/free-regular-svg-icons";
import {
	faBell,
	faChevronDown,
	faCircleCheck,
	faCircleQuestion,
	faCircleXmark,
	faClock,
	faCommentSms,
	faDownload,
	faEnvelope,
	faFileLines,
	faFileExcel,
	faFileCsv,
	faFilePdf,
	faUtensils,
	faNotesMedical,
	faPhone,
	faPlus,
	faTrash,
	faUserGroup,
	faUserPlus,
	faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "../common/Tooltip";
import { PortalMenu } from "../common/PortalMenu";
import { EventAttendeeAddDialog } from "./EventAttendeeAddDialog";
import { useGroupNotify } from "../../hooks/useGroupNotify";
import { useProfileImages } from "../../hooks/useProfileImages";
import { useEventAttendees } from "../../hooks/useEventAttendees";
import {
	ConsentReportFile,
	useConsentReportFile,
} from "../../hooks/useConsentReportFile";
import { Attendee, AttendeeDecision, EventDoc } from "../../types/event";
import { buildDietaryCsv, dietaryRowCount } from "../../utils/dietaryCsv";
import { app } from "../../config";
import "./eventManager.css";

interface EventAttendeesTabProps {
	event: EventDoc;
	/**
	 * Whether the signed-in user holds `event_admin`.
	 *
	 * Reading the roll is open to any active staff member; every write needs
	 * `event_admin`. The API enforces that itself — this only decides whether to
	 * offer an action that would be refused.
	 */
	canEdit?: boolean;
	/** Switches the editor to the Groups tab — the bulk route for attendees. */
	onAddAttendees?: () => void;
}

/**
 * Attendees tab — laid out from the Event Manager design: search and the four
 * actions across the top, then a roll with the carer contact columns, approval
 * status and the per-student medical report.
 *
 * ⚠️ PII. Rows carry student names, Edumate student numbers, and — new in this
 * version — carer names, emails and phone numbers. The previous implementation
 * deliberately withheld the carer columns; the design calls for them, so they
 * are rendered here and are reachable via mailto:/tel:/sms: links. Rules still
 * applied:
 *   - rendered straight from API state; nothing is copied into other stores
 *   - nothing is written to localStorage/sessionStorage or the URL
 *   - no console output of attendee data, including in error paths
 *   - the Excel export is user-initiated and downloads locally; it is never
 *     posted anywhere
 *
 * Approvals ARE editable here, via PATCH. This reverses an earlier decision in
 * this file that they should not be: the reasoning was that a decision belongs
 * to the carer, which is true, but the office still has to be able to record
 * one taken over the phone or on paper. The API treats it as an administrative
 * override and stamps it with the staff member who made it, so the change is
 * attributable rather than anonymous. Setting a decision is deliberately two
 * clicks through a menu, not a stray click on a chip.
 */

/** Row shape the table renders, mapped from the API's attendee document. */
interface AttendeeRow {
	id: string;
	surname: string;
	/** The preferred name — what the roll shows and sorts on. */
	givenName: string;
	/** The legal given name. Searchable, never displayed. */
	legalGivenName: string;
	fullName: string;
	/** No Edumate student number, so not a student. See `rows`. */
	isStaff: boolean;
	boarder: boolean;
	studentNumber: string;
	compassGroup: string;
	carers: { name: string; email: string; phone: string }[];
	approval: string;
	approvedAt: string;
	formSubmitted: boolean;
	medicalFormUrl: string;
	/** Free text, HIGH sensitivity. Shown only in a tooltip, never inline. */
	dietary: string;
}

/**
 * Which part of the roll is showing. Students is the default because that is
 * what the tab showed before staff rows were kept, and what the approval,
 * carer and medical columns are for.
 */
type Audience = "students" | "staff" | "all";

const AUDIENCE_SEGMENTS: CCSegmentedOption[] = [
	{ value: "students", label: "Students" },
	{ value: "staff", label: "Staff" },
	{ value: "all", label: "All" },
];

/**
 * Approval state to badge tone, following the design's colour language: green
 * approved, amber review, red pending. Keys are lower-cased before lookup.
 *
 * Only `Approved`, `Not Approved` and empty are actually stored against a
 * decision today (v2's editable grid offers exactly those three). `Pending` and
 * `Review` come from the design and are mapped defensively so they render
 * correctly rather than falling through to the default if they ever appear.
 */
const APPROVAL_TONES: Record<string, CCBadgeTone> = {
	approved: "success",
	going: "success",
	review: "warning",
	pending: "danger",
	"not approved": "danger",
	"not going": "danger",
	declined: "danger",
};

/**
 * The glyph for each tone, so the icon and the colour cannot disagree.
 *
 * Keyed on TONE rather than on the decision text, which means the eight
 * decision spellings above collapse to three marks: approved, refused, and
 * waiting. A new spelling picks up an icon by picking up a tone.
 *
 * Colour is never the only signal — the shape differs too — because a tick and
 * a cross that are the same glyph in green and red are one signal to anyone
 * with a red-green deficiency, on a column that says whether a child may go.
 */
const STATUS_ICONS: Partial<Record<CCBadgeTone, IconDefinition>> = {
	success: faCircleCheck,
	danger: faCircleXmark,
	warning: faClock,
};

const EDUMATE_REPORT =
	"https://edumate.pymblelc.nsw.edu.au/pymble4/pages/office/enrolments/generate_one_click_report.php";

const EDUMATE_CONTACT =
	"https://edumate.pymblelc.nsw.edu.au/pymble/web/app.php/admin/search-result/#contact/";

/** `04xx xxx xxx` / `0x xxxx xxxx`, matching v2's formatPhone. */
function formatPhone(phone: string | null | undefined): string {
	if (!phone) return "";
	const digits = phone.replace(/\D/g, "");
	if (!digits) return "";
	if (digits.startsWith("04")) {
		return [digits.slice(0, 4), digits.slice(4, 7), digits.slice(7, 10)]
			.filter(Boolean)
			.join(" ");
	}
	return [digits.slice(0, 4), digits.slice(4, 10)].filter(Boolean).join(" ");
}

/**
 * Full local timestamp, e.g. "30 Aug 2026, 4:00 pm".
 *
 * Shown on hover over the status chip. This is the whole of what the Approval
 * Date column used to carry, so nothing was lost in dropping it — the short
 * "30 Aug" form it displayed was strictly less information.
 */
function formatStamp(iso: string): string {
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return "";
	return d.toLocaleString("en-AU", {
		day: "numeric",
		month: "short",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});
}

/**
 * Help text under both photo consent items. The files list everyone going, so
 * they can name people the roll below does not show.
 */
const CONSENT_FILE_HELP =
	"Includes everyone going, including members of selected groups.";

/** Local `YYYY-MM-DD` for the report's Event_Start_Date parameter. */
function reportDate(iso: string | undefined): string {
	if (!iso) return "";
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return "";
	const pad = (n: number) => String(n).padStart(2, "0");
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export const EventAttendeesTab: React.FC<EventAttendeesTabProps> = ({
	event,
	canEdit = false,
	onAddAttendees,
}) => {
	const [query, setQuery] = useState("");
	const [audience, setAudience] = useState<Audience>("students");
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const [notifyOpen, setNotifyOpen] = useState(false);
	const [notify, setNotify] = useState({ title: "", body: "", error: "" });
	const [exporting, setExporting] = useState(false);
	const [addOpen, setAddOpen] = useState(false);
	const [confirmRemove, setConfirmRemove] = useState<{
		id: string;
		name: string;
	} | null>(null);

	const toast = useToast();
	const { sendToGroups, notificationSending } = useGroupNotify();
	const { imageUrl } = useProfileImages();
	const { download: downloadConsentFile, preparing: consentPreparing } =
		useConsentReportFile(event._id);

	const {
		attendees: fetchedAttendees,
		attendeesLoading,
		attendeesError,
		addAttendees,
		attendeesAdding,
		updateAttendee,
		removeAttendee,
	} = useEventAttendees(event._id);

	/**
	 * The roll comes from `GET .../attendees/`, not from the copy embedded in the
	 * event document.
	 *
	 * The two are the same data, but only the endpoint reflects a write — adding
	 * or removing someone leaves the parent document's `Attendees` array stale
	 * until the whole event is refetched, so a table fed from it would show the
	 * old roll straight after a change.
	 *
	 * The embedded copy is still used as the first paint: it has usually already
	 * arrived with the event, so the table renders immediately instead of
	 * flashing empty while the roll loads. Once the endpoint answers, it wins.
	 */
	const attendees: Attendee[] = useMemo(() => {
		if (!attendeesLoading && !attendeesError) return fetchedAttendees;
		return Array.isArray(event.Attendees) ? event.Attendees : [];
	}, [fetchedAttendees, attendeesLoading, attendeesError, event.Attendees]);

	/**
	 * Map the API document to display fields.
	 *
	 * Names come from the discrete `GivenName` / `FamilyName` fields. The
	 * combined `Name` ("FamilyName, GivenName") is only a fallback for a
	 * deployment that predates them: splitting it on a comma breaks on any name
	 * that legitimately contains one, which is exactly why the API added the
	 * separate fields.
	 *
	 * `Boarder` is a StudentInfo.Type string rather than a boolean, and carers
	 * arrive as an array already ordered by CallOrder.
	 *
	 * Attendees with no student number used to be dropped, as in v2. They are
	 * mostly staff: the API resolves every contact linked to the event, and a
	 * staff member has no Edumate student number. They are kept now and marked
	 * `isStaff` so the audience toggle can show them. `Type` is not the test —
	 * it says supervising or taking part, and a staff PD event has staff taking
	 * part.
	 */
	const rows: AttendeeRow[] = useMemo(() => {
		const startDate = reportDate(event.StartDateTime);
		const eventId = event.EventId ?? "";

		return (
			attendees
				.map((a) => {
					const [splitSurname = "", splitGiven = ""] = (a.Name ?? "")
						.split(",")
						.map((part) => part.trim());
					const surname = a.FamilyName ?? splitSurname;
					/* PREFERRED FIRST. `GivenName` is the legal name, and preferring
					   it meant the roll called a girl something nobody calls her —
					   including the teacher supervising the excursion, who is reading
					   this to take a roll out loud.

					   `a.Name` is already `"FamilyName, PreferredName"`, so the split
					   fallback carries the preferred name too and only matters where
					   neither discrete field arrived. The legal name is still on the
					   wire in `GivenName` for anyone matching a row back to Edumate;
					   it is simply not what is displayed. */
					const givenName = a.PreferredName || a.GivenName || splitGiven;

					// Carers show as salutation plus full name, i.e. the API's `Name`.
					// The discrete GivenName/FamilyName are only the fallback for
					// building it if `Name` is ever absent.
					const carers = (a.Carers ?? []).map((c) => ({
						name:
							c.Name || [c.GivenName, c.FamilyName].filter(Boolean).join(" "),
						email: c.Email ?? "",
						phone: formatPhone(c.Phone),
					}));

					return {
						id: a.id,
						surname,
						givenName,
						/* Kept off the screen but inside the search. Now that the roll
						   displays the preferred name, someone typing the legal name
						   they have from Edumate would otherwise find nobody. */
						legalGivenName: a.GivenName ?? "",
						fullName: [givenName, surname].filter(Boolean).join(" "),
						isStaff: !a.EdumateStudentNumber,
						// `Boarder` is StudentInfo.Type; anything containing "Day" is
						// a day student. Same test v2 uses.
						boarder: !!a.Boarder && !a.Boarder.includes("Day"),
						studentNumber: a.EdumateStudentNumber ?? "",
						compassGroup: a.CompassGroup ?? "",
						carers,
						approval: a.Attending ?? "",
						approvedAt: a.ApprovedAt ?? "",
						formSubmitted: !!a.FormsSubmitted,
						dietary: (a.DietaryRequirements ?? "").trim(),
						medicalFormUrl:
							eventId && a.EdumateStudentNumber
								? `${EDUMATE_REPORT}?modRepId=2713&modRepDisp=inline` +
									`&modRepName=medical_report&module=General` +
									`&Event_Start_Date=${startDate}` +
									`&Student_Number=${a.EdumateStudentNumber}` +
									`&EventID=${eventId}`
								: "",
					};
				})
				// Given name, then surname to break ties. Sorted here rather than in
				// `filtered` so the Excel export comes out in the same order as the
				// screen. localeCompare so accented names file where a reader expects
				// them rather than after Z.
				.sort(
					(a, b) =>
						a.givenName.localeCompare(b.givenName, "en-AU") ||
						a.surname.localeCompare(b.surname, "en-AU"),
				)
		);
	}, [attendees, event.StartDateTime, event.EventId]);

	/** The roll narrowed to the chosen audience, before the search. */
	const audienceRows = useMemo(
		() =>
			audience === "all"
				? rows
				: rows.filter((r) => r.isStaff === (audience === "staff")),
		[rows, audience],
	);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return audienceRows;
		return audienceRows.filter(
			(r) =>
				r.fullName.toLowerCase().includes(q) ||
				r.legalGivenName.toLowerCase().includes(q) ||
				r.studentNumber.toLowerCase().includes(q) ||
				r.compassGroup.toLowerCase().includes(q) ||
				r.carers.some((c) => c.name.toLowerCase().includes(q)),
		);
	}, [audienceRows, query]);

	const approvedCount = useMemo(
		() =>
			audienceRows.filter(
				(r) => APPROVAL_TONES[r.approval.toLowerCase()] === "success",
			).length,
		[audienceRows],
	);

	/**
	 * The event's groups are what the notification targets — the notify API
	 * takes group ids, not contacts.
	 */
	/**
	 * The event's own audience groups — the attendees and their carers — not
	 * `SelectedGroups`, which is the list of groups the event was built from.
	 *
	 * Both are sent. notice-manager splits `group_id` on commas and resolves
	 * each one, so a notice reaches students and parents together.
	 * `_id` is the key it looks up; the numeric `GroupId` is a different
	 * identifier and will not resolve.
	 */
	const attendeesGroupId = event.EventGroups?.Attendees?._id;
	const carersGroupId = event.EventGroups?.Parents?._id;

	/** Both audience groups, for the notification (which targets groups). */
	const groupIds = useMemo(
		() =>
			[attendeesGroupId, carersGroupId].filter(
				(id): id is string => typeof id === "string" && id.length > 0,
			),
		[attendeesGroupId, carersGroupId],
	);

	// Notifying or creating a notice for an event that has already finished
	// sends parents somewhere pointless, so both are closed off afterwards.
	const eventPassed = useMemo(() => {
		if (!event.EndDateTime) return false;
		const end = new Date(event.EndDateTime);
		return !Number.isNaN(end.getTime()) && end < new Date();
	}, [event.EndDateTime]);

	const hasEdumate = !!(
		event.ForeignKeys && "Edumate" in (event.ForeignKeys as object)
	);

	const allSelected =
		filtered.length > 0 && filtered.every((r) => selected.has(r.id));

	const toggleAll = () => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (allSelected) {
				filtered.forEach((r) => next.delete(r.id));
			} else {
				filtered.forEach((r) => next.add(r.id));
			}
			return next;
		});
	};

	const toggleRow = (id: string) => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	/**
	 * Excel export of the roll.
	 *
	 * exceljs is imported on demand: it is roughly a megabyte, and most visits
	 * to this tab never export. Exports the ticked rows, or everything matching
	 * the current search when nothing is ticked.
	 */
	/**
	 * Whether this event has a form for carers to return.
	 *
	 * The column tracks a paper form coming back, so with no form to return it
	 * is a tick that can never mean anything — an empty column on every row of
	 * a grid that already has nine.
	 *
	 * Read from the event prop, which in the editor IS the live draft: typing a
	 * URL on the Permissions tab adds the column, clearing it takes the column
	 * away, without a save in between. Either form counts — a medical form is
	 * still a form somebody has to hand in.
	 */
	const hasExternalForm = useMemo(() => {
		const approval = event.Approval ?? {};
		return !!(
			approval.ExternalPermissionForm?.trim() ||
			approval.ExternalMedicalForm?.trim()
		);
	}, [event.Approval]);

	/** Whether any loaded attendee has dietary text; enables the CSV item. */
	const hasAnyDietary = useMemo(() => rows.some((r) => r.dietary), [rows]);

	/**
	 * The dietary column shows when the event asks for it or the roll already
	 * carries some text; otherwise it is a dead column.
	 */
	const showDietary = useMemo(
		() => event.Approval?.Checks?.Dietary === true || hasAnyDietary,
		[event.Approval, hasAnyDietary],
	);

	/** Local download of the dietary CSV: no carer details, nothing logged. */
	const onDietaryCsv = useCallback(() => {
		const forExport = selected.size
			? filtered.filter((r) => selected.has(r.id))
			: filtered;
		if (!dietaryRowCount(forExport)) {
			toast.show({
				message: "Nothing to export.",
				severity: "warning",
				duration: 3000,
			});
			return;
		}
		const csv = buildDietaryCsv(forExport);
		const url = URL.createObjectURL(
			new Blob([csv], { type: "text/csv;charset=utf-8" }),
		);
		const link = document.createElement("a");
		link.href = url;
		link.download = "event-dietary-requirements.csv";
		link.click();
		URL.revokeObjectURL(url);
	}, [filtered, selected, toast]);

	const onExport = useCallback(async () => {
		const forExport = selected.size
			? filtered.filter((r) => selected.has(r.id))
			: filtered;

		if (!forExport.length) {
			toast.show({
				message: "Nothing to export.",
				severity: "warning",
				duration: 3000,
			});
			return;
		}

		setExporting(true);
		try {
			const ExcelJS = await import("exceljs");
			const book = new ExcelJS.Workbook();
			const sheet = book.addWorksheet("Attendees");

			sheet.columns = [
				{ header: "Surname", key: "surname", width: 18 },
				{ header: "Given name", key: "givenName", width: 18 },
				{ header: "Student no.", key: "studentNumber", width: 14 },
				{ header: "Compass", key: "compassGroup", width: 16 },
				{ header: "Boarder", key: "boarder", width: 10 },
				{ header: "Carer 1", key: "carer1", width: 22 },
				{ header: "Carer 1 email", key: "carer1Email", width: 28 },
				{ header: "Carer 1 phone", key: "carer1Phone", width: 16 },
				{ header: "Carer 2", key: "carer2", width: 22 },
				{ header: "Carer 2 email", key: "carer2Email", width: 28 },
				{ header: "Carer 2 phone", key: "carer2Phone", width: 16 },
				{ header: "Status", key: "approval", width: 14 },
				{ header: "Approval date", key: "approvedAt", width: 22 },
				...(hasExternalForm
					? [{ header: "Form received", key: "form", width: 14 }]
					: []),
			];
			sheet.getRow(1).font = { bold: true };

			forExport.forEach((r) =>
				sheet.addRow({
					surname: r.surname,
					givenName: r.givenName,
					studentNumber: r.studentNumber,
					compassGroup: r.compassGroup,
					boarder: r.boarder ? "Yes" : "No",
					carer1: r.carers[0]?.name ?? "",
					carer1Email: r.carers[0]?.email ?? "",
					carer1Phone: r.carers[0]?.phone ?? "",
					carer2: r.carers[1]?.name ?? "",
					carer2Email: r.carers[1]?.email ?? "",
					carer2Phone: r.carers[1]?.phone ?? "",
					approval: r.approval || "No response",
					approvedAt: r.approvedAt
						? new Date(r.approvedAt).toLocaleString("en-AU")
						: "",
					...(hasExternalForm ? { form: r.formSubmitted ? "Yes" : "No" } : {}),
				}),
			);

			const buffer = await book.xlsx.writeBuffer();
			const url = URL.createObjectURL(
				new Blob([buffer], {
					type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
				}),
			);
			const link = document.createElement("a");
			link.href = url;
			link.download = `${event.Title || "event"}-attendees.xlsx`.replace(
				/[/\\?%*:|"<>]/g,
				"-",
			);
			link.click();
			URL.revokeObjectURL(url);
		} catch {
			// No detail surfaced — the failure could quote row data back.
			toast.show({
				message: "Could not build the export. Please try again.",
				severity: "danger",
				duration: 5000,
			});
		} finally {
			setExporting(false);
		}
	}, [filtered, selected, event.Title, hasExternalForm, toast]);

	/**
	 * The photo consent files are built by the API from everyone going, not from
	 * the rows on screen, so the search box and ticked rows do not narrow them.
	 */
	const onConsentFile = useCallback(
		async (file: ConsentReportFile) => {
			try {
				await downloadConsentFile(file);
			} catch {
				// No detail surfaced, as for the Excel export.
				toast.show({
					message: "Could not download the photo consent file. Please try again.",
					severity: "danger",
					duration: 5000,
				});
			}
		},
		[downloadConsentFile, toast],
	);

	/**
	 * A roll owned by Edumate or Clipboard is rebuilt by that system's sync, so
	 * a change made here can be undone without warning. The API says so on every
	 * write; saying nothing would let someone believe an edit had stuck.
	 */
	const warnIfSyncManaged = useCallback(
		(outcome: { syncManaged: boolean; source: string }) => {
			if (!outcome.syncManaged) return;
			toast.show({
				message: `${outcome.source} owns this event's attendees, so the next sync may undo this.`,
				severity: "warning",
				duration: 8000,
			});
		},
		[toast],
	);

	/**
	 * Add the chosen people.
	 *
	 * A partial result is normal rather than a failure: an id with no matching
	 * contact is skipped and reported while the rest are written. The summary
	 * counts what happened instead of claiming success outright.
	 */
	const onAddPeople = useCallback(
		async (contactIds: string[], type: "Attendee" | "Supervisor") => {
			try {
				const outcome = await addAttendees({ contactIds, type });
				const parts: string[] = [];
				if (outcome.added.length) {
					parts.push(`${outcome.added.length} added`);
				}
				if (outcome.alreadyLinked.length) {
					parts.push(`${outcome.alreadyLinked.length} already on the roll`);
				}
				if (outcome.unknown.length) {
					parts.push(`${outcome.unknown.length} not found`);
				}
				toast.show({
					message: parts.join(", ") || "Nothing to add.",
					severity: outcome.unknown.length ? "warning" : "success",
					duration: 5000,
				});
				warnIfSyncManaged(outcome);
				setAddOpen(false);
				return outcome;
			} catch (error) {
				// Status only. An API error can quote the request back, and the
				// request holds contact ids.
				toast.show({
					message: "Could not add those attendees. Please try again.",
					severity: "danger",
					duration: 5000,
				});
				throw error;
			}
		},
		[addAttendees, toast, warnIfSyncManaged],
	);

	/**
	 * Record a decision, or clear it.
	 *
	 * `null` is sent as a present key so the API clears the decision — leaving
	 * the field out would mean "do not touch it", which is a different thing.
	 */
	const onSetDecision = useCallback(
		async (contactId: string, decision: AttendeeDecision | null) => {
			try {
				warnIfSyncManaged(
					await updateAttendee({ contactId, patch: { Decision: decision } }),
				);
			} catch {
				// No name or id in the message: a toast is on screen long enough to
				// be read over someone's shoulder.
				toast.show({
					message: "Could not save that decision. Please try again.",
					severity: "danger",
					duration: 5000,
				});
			}
		},
		[updateAttendee, toast, warnIfSyncManaged],
	);

	/** Tick or untick "form received". */
	const onSetFormSubmitted = useCallback(
		async (contactId: string, submitted: boolean) => {
			try {
				warnIfSyncManaged(
					await updateAttendee({
						contactId,
						patch: { FormSubmitted: submitted },
					}),
				);
			} catch {
				toast.show({
					message: "Could not update the form status. Please try again.",
					severity: "danger",
					duration: 5000,
				});
			}
		},
		[updateAttendee, toast, warnIfSyncManaged],
	);

	/**
	 * Remove an attendee, once confirmed.
	 *
	 * Confirmed because it is not only an unlink: any decision recorded against
	 * the event goes with it, and re-adding starts from undecided. That is not
	 * recoverable from this screen.
	 */
	const onConfirmRemove = useCallback(async () => {
		if (!confirmRemove) return;
		const { id } = confirmRemove;
		try {
			warnIfSyncManaged(await removeAttendee(id));
			toast.show({
				message: "Attendee removed.",
				severity: "success",
				duration: 4000,
			});
			// Drop it from the tick set too, or the count keeps counting someone
			// who is no longer on the roll.
			setSelected((prev) => {
				const next = new Set(prev);
				next.delete(id);
				return next;
			});
			setConfirmRemove(null);
		} catch {
			toast.show({
				message: "Could not remove that attendee. Please try again.",
				severity: "danger",
				duration: 5000,
			});
		}
	}, [confirmRemove, removeAttendee, toast, warnIfSyncManaged]);

	/**
	 * Opens Notice Manager pre-linked to ONE audience group. `_id` is the key it
	 * resolves via `group/search/?id=`; the numeric `GroupId` will not work.
	 */
	const openNotice = useCallback(
		(groupId: string) => {
			const url =
				`${app.noticesUrl}?event_id=${encodeURIComponent(event._id)}` +
				`&group_id=${encodeURIComponent(groupId)}` +
				`&title=${encodeURIComponent(event.Title ?? "")}`;
			window.open(url, "_blank", "noopener,noreferrer");
		},
		[event._id, event.Title],
	);

	const onSendNotification = useCallback(async () => {
		if (!notify.title.trim() || !notify.body.trim()) {
			setNotify((n) => ({
				...n,
				error: "Give the notification a title and a message.",
			}));
			return;
		}
		if (!groupIds.length) {
			setNotify((n) => ({
				...n,
				error:
					"This event has no attendee or carer group yet, so there is nobody to notify.",
			}));
			return;
		}
		try {
			await sendToGroups({
				Method: "push",
				Title: notify.title.trim(),
				Body: notify.body.trim(),
				Groups: groupIds,
			});
			setNotifyOpen(false);
			setNotify({ title: "", body: "", error: "" });
			toast.show({
				message: "Notification sent.",
				severity: "success",
				duration: 3000,
			});
		} catch {
			setNotify((n) => ({
				...n,
				error: "Could not send the notification. Please try again.",
			}));
		}
	}, [notify.title, notify.body, groupIds, sendToGroups, toast]);

	return (
		/* em-att-pane makes this tab a flex column that fills the tab panel, so
		   the table below can be the thing that scrolls rather than the panel.
		   That keeps the sticky header working — sticky resolves against the
		   nearest scrolling ancestor, so the scroller has to be the box the
		   header lives in. */
		<div className="em-att-pane">
			<div className="em-att-toolbar">
				{/* Search and the counts it drives sit together on the left; the
				    actions group to the right. */}
				<div className="em-att-toolbar__left">
					<div className="em-att-search">
						<CCField
							label="Search"
							type="search"
							size="sm"
							fullWidth
							value={query}
							onChange={(e) => setQuery(e.target.value)}
						/>
					</div>

					<CCSegmented
						label="Show attendees"
						options={AUDIENCE_SEGMENTS}
						value={audience}
						onSelect={(v) => {
							setAudience(v as Audience);
							/* A ticked row the new view hides would still be counted
							   and exported, with nothing on screen to say so. */
							setSelected(new Set());
						}}
					/>

					<div className="em-att-counts">
						<CCBadge tone="info">{audienceRows.length} invited</CCBadge>
						<CCBadge tone="success">{approvedCount} approved</CCBadge>
						{selected.size > 0 && (
							<CCBadge tone="neutral">{selected.size} selected</CCBadge>
						)}
						{query && (
							<CCBadge tone="neutral">{filtered.length} matching</CCBadge>
						)}
					</div>
				</div>

				<div className="em-att-toolbar__actions">
					<CCButton
						variant="outline"
						size="sm"
						disabled={eventPassed}
						title={
							eventPassed
								? "This event has finished, so notifications are closed."
								: undefined
						}
						onClick={() => setNotifyOpen(true)}
					>
						<FontAwesomeIcon icon={faBell} /> Send Notification
					</CCButton>

					{/* The photo consent items carry a second line of help text and sit
					    after a divider. CCMenu items take a plain-string label and no
					    separator, so both come from `.em-download-menu` in
					    eventManager.css: the label's "\n" breaks the line, and the
					    divider rides on the first of the last two rows. Keep the
					    consent items last. */}
					<CCMenu
						align="start"
						className="em-download-menu"
						trigger={
							<CCButton
								variant="outline"
								size="sm"
								endIcon={<FontAwesomeIcon icon={faChevronDown} />}
							>
								<FontAwesomeIcon icon={faDownload} /> Download
							</CCButton>
						}
						items={[
							{
								id: "excel",
								label: exporting ? "Preparing…" : "Excel Export",
								icon: <FontAwesomeIcon icon={faFileExcel} />,
								disabled: exporting,
								onSelect: () => void onExport(),
							},
							{
								id: "dietary",
								label: "Dietary requirements (csv)",
								icon: <FontAwesomeIcon icon={faFileCsv} />,
								disabled: !hasAnyDietary,
								onSelect: onDietaryCsv,
							},
							{
								id: "medical",
								label: "Medical Form",
								icon: <FontAwesomeIcon icon={faNotesMedical} />,
								// The report is generated by Edumate from its own event
								// id, so it only exists for Edumate-sourced events.
								disabled: !hasEdumate || !event.EventId,
								onSelect: () =>
									window.open(
										`${EDUMATE_REPORT}?modRepId=3179&modRepDisp=inline` +
											`&modRepName=medical_report&module=General` +
											`&Student_Number=&EventID=${event.EventId}`,
										"_blank",
										"noopener,noreferrer",
									),
							},
							{
								id: "consent-excel",
								label:
									consentPreparing === "export"
										? "Preparing…"
										: `Photo consent (Excel)\n${CONSENT_FILE_HELP}`,
								icon: <FontAwesomeIcon icon={faFileExcel} />,
								disabled: !event._id || consentPreparing !== null,
								onSelect: () => void onConsentFile("export"),
							},
							{
								id: "consent-pdf",
								label:
									consentPreparing === "print"
										? "Preparing…"
										: `Photo consent sheet (PDF)\n${CONSENT_FILE_HELP}`,
								icon: <FontAwesomeIcon icon={faFilePdf} />,
								disabled: !event._id || consentPreparing !== null,
								onSelect: () => void onConsentFile("print"),
							},
						]}
					/>

					{/* Two ways in, because there are two jobs. The menu's first item
				    adds named people through the roll endpoint; the second is the
				    old behaviour — go to Groups, which is still how a whole cohort
				    is invited. Without event_admin neither write is allowed, so the
				    button degrades to the Groups link on its own. */}
					{canEdit ? (
						<CCMenu
							align="end"
							trigger={
								<CCButton
									variant="outline"
									colour="success"
									size="sm"
									endIcon={<FontAwesomeIcon icon={faChevronDown} />}
								>
									<FontAwesomeIcon icon={faUserPlus} /> Add Attendee
								</CCButton>
							}
							items={[
								{
									id: "add-person",
									label: "Search for a person…",
									icon: <FontAwesomeIcon icon={faUserPlus} />,
									onSelect: () => setAddOpen(true),
								},
								{
									id: "add-group",
									label: "Add a group",
									icon: <FontAwesomeIcon icon={faUserGroup} />,
									disabled: !onAddAttendees,
									onSelect: () => onAddAttendees?.(),
								},
							]}
						/>
					) : (
						<CCButton
							variant="outline"
							colour="success"
							size="sm"
							onClick={onAddAttendees}
							title="Attendees come from the groups linked to this event"
						>
							<FontAwesomeIcon icon={faUserPlus} /> Add Attendee
						</CCButton>
					)}

					{/* A notice goes to one audience, so this picks which: the
				    students or their carers. Each opens Notice Manager
				    pre-linked to that group. */}
					<CCMenu
						align="end"
						trigger={
							/* A real CCButton, not a hand-rolled <button> with copied
						   classes: the solid look comes from the base .cc-btn rule
						   and there is no .cc-btn--solid to replicate, so the
						   copy rendered flat. CCMenu clones whatever element it
						   is given, so the component works directly. */
							<CCButton
								variant="solid"
								colour="primary"
								size="sm"
								endIcon={<FontAwesomeIcon icon={faChevronDown} />}
								disabled={eventPassed}
								title={
									eventPassed
										? "This event has finished, so a notice cannot be created."
										: undefined
								}
							>
								<FontAwesomeIcon icon={faPlus} /> Create Notice
							</CCButton>
						}
						items={[
							{
								id: "notice-attendees",
								label: "Attendees",
								icon: <FontAwesomeIcon icon={faUsers} />,
								disabled: !attendeesGroupId,
								onSelect: () =>
									attendeesGroupId && openNotice(attendeesGroupId),
							},
							{
								id: "notice-carers",
								label: "Carers",
								icon: <FontAwesomeIcon icon={faUserGroup} />,
								disabled: !carersGroupId,
								onSelect: () => carersGroupId && openNotice(carersGroupId),
							},
						]}
					/>
				</div>
			</div>

			{rows.length === 0 ? (
				<div className="em-picker-empty">
					{attendeesLoading
						? "Loading attendees…"
						: attendeesError
							? "Could not load the attendees for this event."
							: canEdit
								? "No attendees for this event yet. Add people individually, or link a group on the Groups tab."
								: "No attendees for this event yet. Attendees are derived from the groups linked to the event."}
				</div>
			) : (
				<div className="em-att-table-wrap">
					<table className="em-att-table">
						<thead>
							<tr>
								<th className="em-att-check">
									<input
										type="checkbox"
										checked={allSelected}
										aria-label="Select all attendees"
										onChange={toggleAll}
									/>
								</th>
								<th className="em-att-left">Name</th>
								<th>Stud No.</th>
								<th>Compass</th>
								<th className="em-att-left">Carer 1</th>
								<th>Contact</th>
								<th className="em-att-left">Carer 2</th>
								<th>Contact</th>
								<th>Status</th>
								<th>Medical</th>
								{showDietary && <th>Dietary</th>}
								{canEdit && (
									<>
										{/* Labelled, unlike the actions column beside
										    it: an unlabelled tickbox on the right of a
										    row that already has a select tickbox on the
										    left reads as a second way to select.
										    
										    Only when the event has a form to return —
										    see `hasExternalForm`. */}
										{hasExternalForm && <th>Form</th>}
										<th>
											<span className="em-sr-only">Actions</span>
										</th>
									</>
								)}
							</tr>
						</thead>
						<tbody>
							{filtered.map((r) => {
								// Boarding status is called out here as well as in its
								// own column, so hovering the photo answers "who is
								// this, and do they board?" in one go.
								const personLabel = r.boarder
									? `${r.fullName} - Boarder`
									: r.fullName;
								return (
									<tr
										key={r.id}
										className={
											[
												r.isStaff && "is-staff",
												selected.has(r.id) && "is-selected",
											]
												.filter(Boolean)
												.join(" ") || undefined
										}
									>
										<td className="em-att-check">
											<input
												type="checkbox"
												checked={selected.has(r.id)}
												aria-label={`Select ${r.fullName}`}
												onChange={() => toggleRow(r.id)}
											/>
										</td>
										{/* Both columns are narrow enough to clip most names,
									    so each resolves to the full name on hover.

									    The photo and the surname share ONE tooltip rather
									    than carrying one each: hovering either shows the
									    same thing, and a screen reader hears the name
									    once instead of twice. */}
										<td className="em-att-left">
											<Tooltip
												label={personLabel}
												/* Same photo as the 26px one inline, shown at 72px — the
											   roll's avatar is too small to recognise anyone by, which
											   is the whole reason to hover it. */
												avatar={{ src: imageUrl(r.id), name: r.fullName }}
												className="em-att-person"
											>
												{/* `id` is the ContactPersonal id, which is
											    what the photo is filed under. No photo on
											    file just falls back to initials. */}
												<CCAvatar
													src={imageUrl(r.id)}
													name={r.fullName}
													size={26}
													decorative
													className={
														r.boarder ? "em-att-avatar--boarder" : undefined
													}
												/>
												<span className="em-att-name">{r.fullName}</span>
											</Tooltip>
										</td>
										<td>
											{/* The number is a link to Edumate and did not look
											    like one: table links here carry no underline until
											    hover, so a column of navy digits reads as data.
											    `title` says where it goes, matching how the carer
											    actions in this table announce themselves.

											    The accessible name keeps the NUMBER — a screen
											    reader hearing "View in Edumate" on every row of a
											    hundred learns nothing about which row it is on. */}
											{r.studentNumber && (
												<a
													href={`${EDUMATE_CONTACT}${r.studentNumber}`}
													target="_blank"
													rel="noopener noreferrer"
													title="View in Edumate"
													aria-label={`View student ${r.studentNumber} in Edumate`}
												>
													{r.studentNumber}
												</a>
											)}
										</td>
										<td>{r.compassGroup}</td>
										{/* Each carer is two columns: the name, then its own
									    contact column. Keeping the icons out of the name
									    cell stops a long name squeezing them and means
									    the actions line up down the table. */}
										{[0, 1].map((i) => {
											const carer = r.carers[i];
											return (
												<React.Fragment key={i}>
													<td className="em-att-left">
														{carer ? (
															<Tooltip
																label={carer.name}
																className="em-att-carer-name"
															>
																{carer.name}
															</Tooltip>
														) : (
															<span className="em-muted">—</span>
														)}
													</td>
													<td className="em-att-contact">
														{carer && (
															<span className="em-att-carer-actions">
																{carer.email && (
																	<a
																		className="em-att-action--email"
																		href={`mailto:${carer.email}`}
																		title={`Email ${carer.name}`}
																		aria-label={`Email ${carer.name}`}
																	>
																		<FontAwesomeIcon icon={faEnvelope} />
																	</a>
																)}
																{carer.phone && (
																	<>
																		<a
																			className="em-att-action--call"
																			href={`tel:${carer.phone.replace(/\s/g, "")}`}
																			title={`Call ${carer.name}`}
																			aria-label={`Call ${carer.name}`}
																		>
																			<FontAwesomeIcon icon={faPhone} />
																		</a>
																		<a
																			className="em-att-action--chat"
																			href={`sms:${carer.phone.replace(/\s/g, "")}`}
																			title={`Text ${carer.name}`}
																			aria-label={`Text ${carer.name}`}
																		>
																			<FontAwesomeIcon icon={faCommentSms} />
																		</a>
																	</>
																)}
															</span>
														)}
													</td>
												</React.Fragment>
											);
										})}
										{/* No decision yet shows nothing at all — an empty cell
									    reads as "not answered" without spending a chip saying so.
									    When there is one, the chip carries WHEN it was made on
									    hover, which is what the Approval Date column used to do. */}
										<td className="em-att-status">
											{(() => {
												// No decision reads as "not answered" without
												// spending a chip saying so — but an editor
												// still needs something to aim at, so it
												// becomes a quiet "Set" instead of nothing.
												const tone =
													APPROVAL_TONES[r.approval.toLowerCase()] ?? "warning";

												/* The decision now reads as a mark rather than a
												   word. Nine columns of chips made the roll a wall
												   of text where the only question being asked is
												   "yes, no, or not yet", which a glyph answers at a
												   glance down the column.

												   The WORD does not disappear, it moves into the
												   tooltip, which is also the icon's accessible name
												   — see Tooltip's `label`. Always wrapped now, not
												   only when there is a timestamp, because with the
												   text gone the tooltip is the only thing naming
												   the state. */
												const label = r.approvedAt
													? `${r.approval}, ${formatStamp(r.approvedAt)}`
													: r.approval;

												const chip = r.approval ? (
													<Tooltip label={label}>
														<span className={`em-att-status-icon is-${tone}`}>
															<FontAwesomeIcon
																icon={STATUS_ICONS[tone] ?? faCircleQuestion}
															/>
														</span>
													</Tooltip>
												) : canEdit ? (
													/* An empty circle, not the word "Set". It keeps the
													   column one shape wide, so the eye runs down a
													   line of marks rather than stepping around a pill
													   on every undecided row — and an outline against
													   three filled glyphs reads as "nothing here yet"
													   without being read.

													   Still only for an editor. Someone who cannot
													   change it gets an empty cell, which says the same
													   thing without offering a target. */
													<Tooltip label="No decision yet">
														<span className="em-att-status-icon em-att-status-icon--empty">
															<FontAwesomeIcon icon={faCircle} />
														</span>
													</Tooltip>
												) : null;

												if (!chip) return null;

												const withDate = chip;

												if (!canEdit) return withDate;

												return (
													/* PortalMenu, not CCMenu: this one sits inside
													   `.em-att-table-wrap`, which is `overflow: auto`
													   so the roll can scroll under its sticky header,
													   and an overflow box clips an absolutely
													   positioned popup. The menu opened cut off at the
													   table's edge and its options could not be read.
													   Everything else about it is the kit's menu. */
													<PortalMenu
														align="end"
														trigger={
															<button
																type="button"
																className="em-att-status-btn"
																aria-label={`Approval for ${r.fullName}: ${
																	r.approval || "not set"
																}. Change it.`}
															>
																{withDate}
															</button>
														}
														items={[
															{
																id: "approved",
																label: "Approved",
																onSelect: () =>
																	void onSetDecision(r.id, "Approved"),
															},
															{
																id: "not-approved",
																label: "Not Approved",
																onSelect: () =>
																	void onSetDecision(r.id, "Not Approved"),
															},
															{
																id: "clear",
																label: "Clear decision",
																// Nothing to clear yet.
																disabled: !r.approval,
																onSelect: () => void onSetDecision(r.id, null),
															},
														]}
													/>
												);
											})()}
										</td>
										<td>
											{r.medicalFormUrl && (
												<a
													href={r.medicalFormUrl}
													target="_blank"
													rel="noopener noreferrer"
													title={`Medical form for ${r.fullName}`}
													aria-label={`Medical form for ${r.fullName}`}
													className={
														r.formSubmitted
															? "em-att-medical is-submitted"
															: "em-att-medical"
													}
												>
													<FontAwesomeIcon icon={faFileLines} />
												</a>
											)}
										</td>
										{showDietary && (
											<td>
												{r.dietary && (
													<span
														className="em-att-dietary"
														title={r.dietary}
														aria-label={r.dietary}
													>
														<FontAwesomeIcon icon={faUtensils} />
													</span>
												)}
											</td>
										)}
										{canEdit && (
											<>
												{/* "Form received" is a tick rather than a menu
										    item: it is a yes/no the office flips often,
										    and burying a frequent two-state toggle in a
										    menu costs a click every time. */}
												{hasExternalForm && (
													<td>
														<label
															className="em-att-formcheck"
															title={`Form received for ${r.fullName}`}
														>
															<input
																type="checkbox"
																checked={r.formSubmitted}
																aria-label={`Form received for ${r.fullName}`}
																onChange={(e) =>
																	void onSetFormSubmitted(
																		r.id,
																		e.target.checked,
																	)
																}
															/>
														</label>
													</td>
												)}
												<td className="em-att-rowactions">
													<button
														type="button"
														className="em-att-remove"
														title={`Remove ${r.fullName}`}
														aria-label={`Remove ${r.fullName} from this event`}
														onClick={() =>
															setConfirmRemove({
																id: r.id,
																name: r.fullName,
															})
														}
													>
														<FontAwesomeIcon icon={faTrash} />
													</button>
												</td>
											</>
										)}
									</tr>
								);
							})}
						</tbody>
					</table>

					{filtered.length === 0 && (
						<div className="em-picker-empty">
							{query
								? "No attendees matched that search."
								: audience === "staff"
									? "No staff on this event."
									: audience === "students"
										? "No students on this event."
										: "No attendees on this event."}
						</div>
					)}
				</div>
			)}

			<CCModal
				open={notifyOpen}
				onClose={() => setNotifyOpen(false)}
				title="Send notification"
				footer={
					<>
						<CCButton
							variant="outline"
							onClick={() => setNotifyOpen(false)}
							disabled={notificationSending}
						>
							Cancel
						</CCButton>
						<CCButton
							variant="solid"
							colour="primary"
							loading={notificationSending}
							onClick={onSendNotification}
						>
							Send
						</CCButton>
					</>
				}
			>
				<div className="em-att-notify">
					<p className="em-muted">
						Sent as a push notification to this event's attendees and their
						carers. It goes to whole groups, so it cannot be targeted at
						individual attendees.
					</p>
					<CCField
						label="Title"
						fullWidth
						value={notify.title}
						error={notify.error || undefined}
						onChange={(e) =>
							setNotify({ ...notify, title: e.target.value, error: "" })
						}
					/>
					<CCField
						label="Message"
						multiline
						rows={4}
						fullWidth
						value={notify.body}
						onChange={(e) =>
							setNotify({ ...notify, body: e.target.value, error: "" })
						}
					/>
				</div>
			</CCModal>

			<EventAttendeeAddDialog
				open={addOpen}
				saving={attendeesAdding}
				/* Ids already on the roll, so the picker can say so instead of
				   offering a duplicate the API would only report back as
				   AlreadyLinked. Built from `attendees`, the whole roll, rather
				   than the audience or search the table is narrowed to. */
				existingIds={new Set(attendees.map((a) => a.id))}
				onClose={() => setAddOpen(false)}
				onAdd={onAddPeople}
			/>

			{/* Removing also discards any decision recorded against the event, and
			    re-adding starts from undecided — not something to do on a stray
			    click, so it is confirmed by name. */}
			<CCModal
				open={!!confirmRemove}
				onClose={() => setConfirmRemove(null)}
				title="Remove attendee"
				footer={
					<>
						<CCButton variant="outline" onClick={() => setConfirmRemove(null)}>
							Cancel
						</CCButton>
						<CCButton
							variant="solid"
							colour="danger"
							onClick={() => void onConfirmRemove()}
						>
							Remove
						</CCButton>
					</>
				}
			>
				<p>
					Remove <strong>{confirmRemove?.name}</strong> from this event?
				</p>
				<p className="em-muted">
					Any approval recorded against this event is discarded. Adding them
					back starts from undecided.
				</p>
			</CCModal>
		</div>
	);
};
